<style lang="stylus" scoped>
.gpa {
  color: #191919;
  background-color: #F2F2F2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}

.label_heads {
  text-transform: uppercase;
  color: #777777;
  letter-spacing: 0.1em;
  font-size: 11px;
  font-weight: 600;
}

.course-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  gap: 16px;
  align-items: center;
}

.course-name {
  font-size: 16px;
  font-weight: 500;
  color: #191919;
}

.course-credits {
  font-size: 16px;
  font-weight: 500;
  color: #191919;
  text-align: center;
}

.course-grade {
  display: flex;
  justify-content: flex-end;
}

.calculate-section {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;
  align-items: flex-end;
  border-top: 1px solid #f0f0f0;
}

.calculate-button {
  grid-column: 1;
}

.projected-gpa {
  grid-column: 2;
}

@media (max-width: 960px) and (min-width: 601px) {
  .calculate-section {
    grid-template-columns: 3fr 2fr;
  }

  .calculate-button {
    grid-column: 1;
  }

  .projected-gpa {
    grid-column: 2;
  }
}

@media (max-width: 600px) {
  .course-grid {
    grid-template-columns: 1.5fr 0.8fr 1.7fr;
    gap: 12px;
  }

  .calculate-section {
    grid-template-columns: 2.3fr 1.7fr;
    gap: 12px;
  }

  .calculate-button {
    grid-column: 1;
  }

  .projected-gpa {
    grid-column: 2;
  }

  .course-name,
  .course-credits {
    font-size: 14px;
  }
}
</style>
<template>
  <UiCard :title="showTitle ? 'Current GPA' : undefined" :noBorder="noBorder">
    <template v-if="showTitle" #title-right>
      <div class="gpa">{{ GPA }}</div>
    </template>

    <!-- Empty state for HS partner -->
    <div v-if="partnerName === 'hs' && gpaCourses.length <= 0">
      <v-row no-gutters class="mt-2">
        <div><img src="../assets/icons/calculator.svg" /></div>
        <p class="text-body-2 py-2">
          We encourage you to complete your courses with a 2.75 GPA or better. We look forward to watching you succeed!
        </p>
      </v-row>
    </div>

    <!-- Empty state for non-HS partner -->
    <div v-else-if="gpaCourses.length <= 0">
      <p class="mb-0" style="color: #747474">No courses found. Add courses to start estimating your GPA.</p>
    </div>

    <!-- GPA Calculator -->
    <div v-if="gpaCourses.length > 0">
      <div v-if="$slots.header" class="mb-6">
        <slot name="header" />
      </div>

      <!-- Course Grid Header -->
      <div class="course-grid course-grid-header pb-3">
        <div class="label_heads">COURSE</div>
        <div class="label_heads">CREDITS</div>
        <div class="label_heads">GRADE</div>
      </div>

      <!-- Course Rows -->
      <div v-for="(course, index) in gpaCourses" :key="index" class="course-grid course-row py-3">
        <div class="course-name">
          {{ course._courseInfo._coursePrefixDisplayLabel }}
        </div>
        <div class="course-credits">
          {{ course._courseInfo._asuCredits }}
        </div>
        <div class="course-grade">
          <v-select
            :items="grades"
            @change="asuoDataLayerClick('CurrentGPA', '0', { text: course._courseInfo.grade })"
            hide-details
            variant="outlined"
            density="compact"
            v-model="course._courseInfo.grade"
          />
        </div>
      </div>

      <!-- Calculate and Result Section -->
      <div class="calculate-section mt-6 pt-6">
        <div class="calculate-button">
          <UiButton variant="dark-outline" size="large" @click="handleCalculateClick"> Calculate </UiButton>
        </div>
        <div class="projected-gpa">
          <div class="label_heads mb-2">PROJECTED GPA</div>
          <v-text-field readonly variant="outlined" density="compact" v-model="projectedGPA" hide-details />
        </div>
      </div>
    </div>
  </UiCard>
</template>
<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import { Card as UiCard, Button as UiButton } from '@/components/ui';

import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
// import helpers to use navigateToURL
export default defineComponent({
  components: {
    UiCard,
    UiButton
  },

  data() {
    return {
      gpaCalculatorPage: 'https://ea.asu.edu/gpa-calculator',
      grades: ['', 'A', 'B', 'C'],
      grade: '',
      projectedGPA: '',
      partnerName: ''
    };
  },

  mixins: [DataLayerMixin],

  props: {
    GPA: {
      type: String,
      required: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    noBorder: {
      type: Boolean,
      default: false
    }
  },

  methods: {
    handleCalculateClick() {
      this.calcGPA();
      this.asuoDataLayerClick('CurrentGPA', '1');
    },
    calcGPA() {
      var projectedPoints = 0;

      const filterArray = this.gpaCourses.filter((user) => {
        return !!user._courseInfo.grade;
      });

      if (filterArray.length == 0) {
        this.projectedGPA = '';
      }

      if (filterArray.length > 0) {
        this.gpaCourses.forEach((e) => {
          if (e._courseInfo.grade === 'A') {
            projectedPoints += 4 * e._courseInfo._asuCredits;
          } else if (e._courseInfo.grade === 'B') {
            projectedPoints += 3 * e._courseInfo._asuCredits;
          } else if (e._courseInfo.grade === 'C') {
            projectedPoints += 2 * e._courseInfo._asuCredits;
          }
        });

        this.projectedGPA =
          (projectedPoints + this.numberOfCreditsCompleted * this.currentGPA) /
          (this.projectedCredits + this.numberOfCreditsCompleted);
        this.projectedGPA = Math.round(this.projectedGPA * 100) / 100;
      }
    }
  },

  computed: {
    projectedCredits() {
      const coursesStore = useCoursesStore();
      return parseFloat(
        coursesStore.gpaCourses.reduce((count, course) => {
          return count + (course._courseInfo.grade ? parseFloat(course._courseInfo._asuCredits) : 0);
        }, 0)
      );
    },
    gpaCourses() {
      const coursesStore = useCoursesStore();
      return coursesStore.gpaCourses || [];
    },
    noOfCourses() {
      return (this.$conn.learnerData || {})._numberOfCourseNeededForAdmission;
    },
    enrolledCourseCount() {
      const coursesStore = useCoursesStore();
      return coursesStore.enrolledCourses.length;
    },
    numberOfCreditsCompleted() {
      return (this.$conn.learnerData || {})._numberOfCreditsCompleted || 0;
    },
    totalCoursesConverted() {
      return this.$conn.learnerData._totalCoursesConverted || 0;
    },
    currentGPA() {
      return parseFloat(this.$conn.learnerData._currentGPA) || 0;
    }
  },

  created() {
    if (this.$conn.learnerData?._partnerURLSubDomain === 'hs') {
      this.partnerName = 'hs';
    }
  }
});
</script>
